// /admin/cookbook-journeys/:id: one cookbook draft. Who made it, the book as it is now,
// how far they got in the wizard, every step with its time, and what reached Klaviyo.
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'

import { getCookbookJourney } from '../../../api/adminV2'
import { formatDate, LoadError, Loading, moneyFromCents, Pill, useLoad } from '../ui'
import { AccountMarks, StepDots, WIZARD_STEPS } from './CookbookJourneys'

const when = (value) => (value ? moment(value).format('MMM D, YYYY [at] h:mm a') : 'n/a')

const NAMES = {
  theme: { classic: 'Classic', modern: 'Modern', heirloom: 'Heirloom' },
  product: { coil: 'Coil bound', hardcover: 'Hardcover' },
  trim_size: { square: 'Square 8.5 x 8.5 in', letter: 'Letter 8.5 x 11 in', trade: '6 x 9 in' },
}
const named = (kind, value) => (value ? NAMES[kind][value] || value : 'Not chosen yet')

const KLAVIYO_STATE = {
  sent: ['good', 'Sent'],
  waiting: ['warn', 'Waiting'],
  not_sent: ['bad', 'Not sent'],
  never_queued: ['bad', 'Never queued'],
}

const ORDER_STATUS = {
  pending_payment: 'On the payment page',
  paid: 'Paid',
  submitted: 'Sent to Lulu',
  in_production: 'Printing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  failed: 'Print problem',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
}

const CookbookJourney = () => {
  const { id } = useParams()
  const { data: j, error, reload } = useLoad(() => getCookbookJourney(id), [id])

  if (error && !j) return <LoadError error={error} onRetry={reload} />
  if (!j) return <Loading />

  const person = j.person || {}
  const reached = j.reached || {}

  return (
    <>
      <div>
        <Link className="a-crumb" to="/admin/cookbook-journeys">
          ← Cookbook journeys
        </Link>
        <div className="a-head">
          <div style={{ minWidth: 0 }}>
            <h1>{j.title || 'Untitled cookbook'}</h1>
            <p className="a-sub">
              Cookbook #{j.id} · started {when(j.started_at)} · last activity{' '}
              {when(j.last_activity_at)}
            </p>
          </div>
          <Pill kind={j.status?.tone}>{j.status?.label}</Pill>
        </div>
      </div>

      <section className="a-panel">
        <div className="a-panel-head">
          <h2>How far they got</h2>
          <StepDots step={j.wizard_step} />
        </div>
        <ol className="a-wizard">
          {WIZARD_STEPS.map((name, i) => (
            <li key={name} className={i < j.wizard_step ? 'a-on' : ''}>
              <b>{i + 1}</b> {name}
            </li>
          ))}
          <li className={reached.paid ? 'a-on' : ''}>Paid</li>
          <li className={reached.printer ? 'a-on' : ''}>At the printer</li>
          <li className={reached.shipped ? 'a-on' : ''}>Shipped</li>
        </ol>
      </section>

      <div className="a-grid2">
        <section className="a-panel">
          <h2>Person</h2>
          <dl className="a-kv">
            <dt>Name</dt>
            <dd>
              {person.id ? (
                <Link to={`/admin/people/${person.id}`}>{person.fullname || '(no name)'}</Link>
              ) : (
                '(account gone)'
              )}{' '}
              <AccountMarks marks={person.marks} />
            </dd>
            <dt>Email</dt>
            <dd>{person.email || 'n/a'}</dd>
            <dt>Plan</dt>
            <dd>
              {person.plan_label || 'None'}{' '}
              {person.status_key ? <Pill status={person.status_key} /> : null}
            </dd>
            <dt>Signed up</dt>
            <dd>{formatDate(person.signed_up_at)}</dd>
            <dt>Family group</dt>
            <dd>
              {j.group ? (
                <Link to={`/admin/groups/${j.group.id}`}>{j.group.name}</Link>
              ) : (
                <span className="a-muted">none (the group may have been deleted)</span>
              )}
            </dd>
          </dl>
        </section>

        <section className="a-panel">
          <h2>The book now</h2>
          <dl className="a-kv">
            <dt>Recipes</dt>
            <dd>
              <span className={j.recipe_count < j.min_recipes ? 'a-short' : ''}>
                {j.recipe_count} of the {j.min_recipes} needed
              </span>
              <span className="a-muted">
                {' '}
                · the group has {j.group_recipe_count} published{' '}
                {j.group_recipe_count === 1 ? 'recipe' : 'recipes'}
              </span>
            </dd>
            <dt>Chapters</dt>
            <dd>
              {j.chapters?.length
                ? j.chapters.map((c) => (
                    <span className="a-tag" key={c.name} style={{ marginRight: 6 }}>
                      {c.name} {c.recipes}
                    </span>
                  ))
                : 'None yet'}
            </dd>
            <dt>Theme</dt>
            <dd>{named('theme', j.theme)}</dd>
            <dt>Edition</dt>
            <dd>{named('product', j.product)}</dd>
            <dt>Size</dt>
            <dd>{named('trim_size', j.trim_size)}</dd>
            <dt>Cover photo</dt>
            <dd>{j.has_cover ? 'Yes' : 'No'}</dd>
            <dt>Dedication</dt>
            <dd>{j.has_dedication ? 'Yes' : 'No'}</dd>
            <dt>Preview</dt>
            <dd>
              {j.render_status
                ? `${j.render_status}${j.page_count ? `, ${j.page_count} pages` : ''}`
                : 'Not built yet'}
            </dd>
            <dt>Blurry photo answers</dt>
            <dd>{j.photo_checks || 'None'}</dd>
          </dl>
        </section>
      </div>

      <section className="a-panel">
        <h2>Timeline</h2>
        {j.timeline?.length ? (
          <ol className="a-timeline">
            {j.timeline.map((e, i) => (
              <li key={`${e.kind}-${i}`} className={`a-tl-${e.tone || 'plain'}`}>
                <span className="a-tl-when">{when(e.at)}</span>
                <span className="a-tl-what">
                  <b>{e.title}</b>
                  {e.detail ? (
                    <span>
                      {e.detail}
                      {e.last_at ? `, last ${when(e.last_at)}` : ''}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="a-empty">Nothing recorded yet.</p>
        )}
        <p className="a-muted a-small-text" style={{ marginTop: 12 }}>
          Not recorded anywhere: opening a step without saving it (for example looking at step 5
          without typing an address), and time spent on each page.
          {j.steps_recorded?.length
            ? ''
            : ' Saving each step is only recorded since this page was added, so older steps show only what the book itself keeps.'}
        </p>
      </section>

      <div className="a-grid2">
        <section className="a-panel">
          <h2>Payments and requests</h2>
          {j.orders?.length || j.requests?.length ? (
            <div className="a-table-wrap">
              <table className="a-table">
                <tbody>
                  {(j.orders || []).map((o) => (
                    <tr key={`o${o.id}`}>
                      <td className="a-who">
                        <b>
                          <Link to={`/admin/cookbook-orders?order=${o.id}`}>Order #{o.id}</Link>
                        </b>
                        <span className="a-line">
                          Opened {when(o.created_at)}
                          {o.paid_at ? ` · paid ${when(o.paid_at)}` : ''}
                        </span>
                        {o.status_detail ? <span className="a-line">{o.status_detail}</span> : null}
                      </td>
                      <td className="a-right">
                        {moneyFromCents(o.charged_cents ?? o.price_cents)}{' '}
                        <Pill kind={o.paid_at ? 'good' : 'warn'}>
                          {ORDER_STATUS[o.status] || o.status}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                  {(j.requests || []).map((r) => (
                    <tr key={`r${r.id}`}>
                      <td className="a-who">
                        <b>
                          <Link to={`/admin/cookbook-orders?request=${r.id}`}>Request #{r.id}</Link>
                        </b>
                        <span className="a-line">Asked {when(r.created_at)}</span>
                      </td>
                      <td className="a-right">
                        <Pill kind="warn">{r.status}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="a-empty">They have not opened the payment page or asked for the book.</p>
          )}
        </section>

        <section className="a-panel">
          <h2>Klaviyo</h2>
          <div className="a-table-wrap">
            <table className="a-table">
              <tbody>
                {(j.klaviyo || []).map((k) => {
                  const [tone, label] = KLAVIYO_STATE[k.state] || ['plain', k.state]
                  return (
                    <tr key={k.key}>
                      <td className="a-who">
                        <b>{k.metric}</b>
                        <span className="a-line">
                          {k.detail}
                          {k.at ? ` · ${when(k.at)}` : ''}
                        </span>
                      </td>
                      <td className="a-right">
                        <Pill kind={tone}>{label}</Pill>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  )
}

export default CookbookJourney
